import {
  requestFacebookLogin,
  socialAuthAvailable,
} from "@/services/socialAuth";
import { setLoadingBar } from "@/redux/siteData";
import { api } from "@/services/api";
import {
  setSocialError,
  setSocialToken,
  setSocialAccountType,
  setStep,
  RegisterStep,
} from "@/redux/register";
import { setUserData, setLoading } from "@/redux/signIn";

export interface RegisterActionCallbacks {
  onExistingUserSignIn?: () => void;
  onStepChange?: (step: RegisterStep) => void;
}

/**
 * Handles Google sign-in or registration flow
 */
export function inlineSignInOrWillCreateGoogleUser(
  credential: string,
  callbacks?: RegisterActionCallbacks
) {
  setLoadingBar(true);
  setSocialError(null);
  api
    .post("/auth/google/token", { credential })
    .then((response) => {
      if (response.status === 200 && response.data) {
        // User exists, sign them in
        setUserData(response.data);
        setLoading(false);
        if (callbacks?.onExistingUserSignIn) {
          callbacks.onExistingUserSignIn();
        } else {
          setStep("joined");
          callbacks?.onStepChange?.("joined");
        }
      } else if (response.status === 204) {
        // User doesn't exist, proceed to username step
        setSocialToken(credential);
        setSocialAccountType("google");
        setStep("socialUsername");
        callbacks?.onStepChange?.("socialUsername");
      }
    })
    .catch((error) => {
      setLoading(false);
      setSocialError("socialSignInFailed");
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Handles Facebook sign-in or registration flow
 */
export function inlineSignInOrWillCreateFB(
  accessToken: string,
  callbacks?: RegisterActionCallbacks
) {
  api
    .post("/auth/facebook/token", { access_token: accessToken })
    .then((response) => {
      if (!response?.data) throw new Error("Empty identity response");
      if (response.data.willCreateFacebookUser) {
        setSocialToken(accessToken);
        setSocialAccountType("facebook");
        setStep("socialUsername");
        callbacks?.onStepChange?.("socialUsername");
      } else {
        setUserData(response.data);
        setLoading(false);
        if (callbacks?.onExistingUserSignIn) {
          callbacks.onExistingUserSignIn();
        } else {
          setStep("joined");
          callbacks?.onStepChange?.("joined");
        }
      }
    })
    .catch(() => {
      setLoading(false);
      setSocialError("socialSignInFailed");
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Initiates Facebook sign-in flow
 */
export function signInFBInline(callbacks?: RegisterActionCallbacks) {
  setLoadingBar(true);
  setSocialError(null);
  requestFacebookLogin(
    (token) => inlineSignInOrWillCreateFB(token, callbacks),
    () => {
      setLoadingBar(false);
      setSocialError("socialSignInFailed");
    }
  );
}

/**
 * Handles Apple sign-in or registration flow
 */
export function inlineSignInOrWillCreateAppleUser(
  token: string,
  callbacks?: RegisterActionCallbacks
) {
  api
    .post("/auth/apple/token", { token })
    .then((response) => {
      if (!response?.data) throw new Error("Empty identity response");
      if (response.data.willCreateAppleUser) {
        setSocialToken(token);
        setSocialAccountType("apple");
        setStep("socialUsername");
        callbacks?.onStepChange?.("socialUsername");
      } else {
        setUserData(response.data);
        setLoading(false);
        if (callbacks?.onExistingUserSignIn) {
          callbacks.onExistingUserSignIn();
        } else {
          setStep("joined");
          callbacks?.onStepChange?.("joined");
        }
      }
    })
    .catch(() => {
      setLoading(false);
      setSocialError("socialSignInFailed");
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Initiates Apple sign-in flow
 */
export async function signInAppleWebInline(
  callbacks?: RegisterActionCallbacks
) {
  setLoadingBar(true);
  try {
    const data = await window.AppleID.auth.signIn();
    if (data.authorization) {
      inlineSignInOrWillCreateAppleUser(data.authorization.id_token, callbacks);
    } else {
      setLoadingBar(false);
    }
  } catch {
    setLoadingBar(false);
    setSocialError("socialSignInFailed");
  }
}

/**
 * Initializes Google Sign-In button
 */
export function initializeGoogleButton(
  currentStep: RegisterStep,
  callbacks?: RegisterActionCallbacks
) {
  // Only initialize when on form step
  if (!socialAuthAvailable || currentStep !== "form") return;

  const g = (window as any).google;
  if (!g?.accounts?.id) return;

  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  if (!clientId) return;

  g.accounts.id.initialize({
    client_id: clientId,
    callback: (res: any) => {
      inlineSignInOrWillCreateGoogleUser(res.credential, callbacks);
    },
    // Use FedCM to avoid popup/opener issues on COOP/COEP pages
    // FedCM does not rely on window.opener postMessage
    use_fedcm_for_prompt: true,
  });

  const gbtn = document.getElementById("gbtn");
  // Match SignIn page button sizing so it doesn't overflow inside NarrowPage padding on mobile.
  let buttonWidth = 0;
  if (window.innerWidth > 480) {
    buttonWidth = Math.min(400, window.innerWidth - 42);
  } else {
    buttonWidth = window.innerWidth - 82;
  }
  if (gbtn) {
    // Clear any existing button content before rendering
    gbtn.innerHTML = "";
    g.accounts.id.renderButton(gbtn, {
      theme: "outline",
      longtitle: true,
      size: "large",
      width: buttonWidth,
      text: "continue_with",
      logo_alignment: "left",
    });
  }
}
